import { Item, Material } from '../types/material';
import { Order } from '../types/order';
import { WeighParams } from '../types/weigh-params';
import { ApiResourceLocation, ResourceService, ResponseResult, Store } from '@softline/core';
import { firstValueFrom, map } from 'rxjs';
import { SOFTLINE_API_WEIGHT, SOFTLINE_API_WEIGHT_GET_UNITS, SOFTLINE_API_WEIGHT_NEW } from '../wiegung.api';
import { MessageBarStore, ModalStore, SOFTLINE_FEATURE_MESSAGE_BAR, SOFTLINE_FEATURE_MODAL } from '@softline/ui-core';
import {
  ExtraMaterialListSelectionDialogComponent
} from '../dialogs/extra-material-list-selection-dialog/extra-material-list-selection-dialog.component';
import { Injector } from '@angular/core';
import { LighterDialogComponent } from '../dialogs/lighter-dialog/lighter-dialog.component';
import { Lagerartikel, Legierung } from '../types/lagerartikel';
import {
  LegierungAuswahlDialogComponent
} from '../dialogs/legierung-auswahl-dialog/legierung-auswahl-dialog.component';
import { LagerartikelDialogComponent } from '../dialogs/lagerartikel-dialog/lagerartikel-dialog.component';

export interface WiegungSubmitParams {
  data: { order: Order, material: Item },
  params: WeighParams | null,
  orderWeight: number
}

export abstract class WiegungService<T> {

  abstract type: 'ERSTWIEGUNG'
    | 'GESAMTWIEGEN'
    | 'MATERIALLIEFERSCHEIN'
    | 'MATERIALLIEFERSCHEIN_RETOUR'
    | 'INVENTUR'
    | 'ZWISCHENWIEGEN';

  abstract isMaterialRequired: boolean;
  abstract isOrderRequired: boolean;

  constructor(
    protected httpService: ResourceService<ApiResourceLocation>,
    protected store: Store,
    protected inj: Injector
  ) {}

  abstract weighMaterial(material: T): void;




  async recordWeight(params: WeighParams): Promise<void> {
    try {
      const location: ApiResourceLocation = {
        path: SOFTLINE_API_WEIGHT
      };

      // await firstValueFrom(this.httpService.create(location, params));

      console.log('[API MOCK]: Gewicht wurde aufgezeichnet');

      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.success,
        {
          title: 'Aufzeichnen der Wiegung erfolgreich!'
        }
      );
    } catch (e) {
      await this.store.dispatch(
        SOFTLINE_FEATURE_MESSAGE_BAR,
        MessageBarStore.actions.error,
        {
          title: 'Aufzeichnen der Wiegung fehlgeschlagen!'
        }
      );
    }
  }

  async weigh(id: number, materialId?: number, simulatedWeight?: number): Promise<WeighParams> {
    const location: ApiResourceLocation = {
      path: SOFTLINE_API_WEIGHT_NEW,
      pathParams: { id },
      queryParams: this.type === 'MATERIALLIEFERSCHEIN' ? { idbeweau: materialId } : undefined
    };

    const params = { gewicht: simulatedWeight ?? null };

    return await firstValueFrom(this.httpService.create(location, params));
  }

  async getUnits(id: number): Promise<{ id: number, arteh: string }[]> {
    const location = this.httpService.get({
      path: SOFTLINE_API_WEIGHT_GET_UNITS,
      pathParams: { id }
    });

    return await firstValueFrom(
      this.httpService.get<ResponseResult<any>>(location).pipe(
        map(o => o.result)
      )
    );
  }

  async getCustomMaterials(): Promise<{ chargen: { charge: string; id: number }[]; artikel: any[] }> {
    return new Promise(resolve => resolve({ chargen: [], artikel: []}));
  }

  async selectCustomMaterial(id: number | string): Promise<Material | 'DISMISSED'> {
    return await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<any, { id: number | string }>(),
      {
        id: 'GOLDSCHMIEDE',
        component: ExtraMaterialListSelectionDialogComponent,
        data: { id },
        getInjector: (): Injector => { return this.inj; },
      }
    );
  }

  async selectCustomLighterMaterial(id: number, gewicht: number): Promise<Material> {
    return await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<any, { id: number; gewicht: number }>(),
      {
        id: 'GOLDSCHMIEDE_LIGHTER_DIALOG',
        component: LighterDialogComponent,
        data: { id, gewicht },
        getInjector: (): Injector => { return this.inj; },
      }
    );
  }

  shouldLoadMaterials(): boolean {
    return this.isMaterialRequired;
  }

  async selectLagerartikel(): Promise<Lagerartikel | null> {
    const result =  await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<Lagerartikel | null, object>(),
      {
        component: LagerartikelDialogComponent,
        getInjector: () => this.inj,
        data: {},
        dismiss: {
          button: true,
          backdrop: false,
          escape: false
        }
      }
    );

    return result === 'DISMISSED' ? null : result;
  }

  async selectLegierungDialog(legierungen: Legierung[]): Promise<Legierung | null> {
    const result = await this.store.dispatch(
      SOFTLINE_FEATURE_MODAL,
      ModalStore.actions.open<Legierung | null, { legierungen: Legierung[] }>(),
      {
        component: LegierungAuswahlDialogComponent,
        getInjector: () => this.inj,
        data: {
          legierungen
        },
        dismiss: {
          button: true,
          backdrop: false,
          escape: false
        }
      }
    );

    return result === 'DISMISSED' ? null : result;
  }

  async submit(params: WiegungSubmitParams): Promise<WeighParams | null> {
    return params.params;
  }
}
